import { addDays, formatDay } from "../../../utils/weekDates";

export const AT_RISK_BURN = 0.85;

// Heat colour for one day: blue under 75% of the person's daily capacity, green up to it, red over it, grey for leave.
export const cellTone = (hours, capacity, onLeave) => {
  if (onLeave) return "leave";
  if (!(hours > 0)) return "none";
  if (hours > capacity) return "over";
  return hours >= capacity * 0.75 ? "ok" : "under";
};

// A person's week: booked vs capacity (less leave days), as shown in the Booked / Capacity column.
export const weekTotals = (person) => {
  const booked = person.days.reduce((sum, d) => sum + d.booked_hours, 0);
  const leaveDays = person.days.filter((d) => d.on_leave).length;
  return { booked, capacity: Math.max(0, person.weekly_hours - leaveDays * person.daily_capacity) };
};

export const sortCapacity = (people, order) => {
  const rows = people.map((p) => ({ person: p, ...weekTotals(p) }));
  return order === "name"
    ? rows.sort((a, b) => a.person.name.localeCompare(b.person.name))
    : rows.sort((a, b) => b.booked - a.booked || a.person.name.localeCompare(b.person.name));
};

// Budget rows for the Time or Cost view, fullest burn first and projects without a budget last.
export const budgetRows = (overview, metric) => {
  const key = metric === "cost" ? "cost" : "hours";
  return overview
    .flatMap((status) => (status.projects || []).map((p) => {
      const budget = Number(p[`budget_${key}`]) || 0;
      const actual = Number(p[`actual_${key}`]) || 0;
      const scheduled = Number(p[`scheduled_${key}`]) || 0;
      const burn = budget > 0 ? actual / budget : null;
      return { ...p, status_name: status.status_name, status_color: status.status_color, budget, actual, scheduled, burn, atRisk: Boolean(p.is_overdue) || (burn !== null && burn >= AT_RISK_BURN) };
    }))
    .sort((a, b) => (b.burn ?? -1) - (a.burn ?? -1));
};

// Mon–Fri label such as "12 – 16 Oct", or "29 Sep – 3 Oct" across a month end.
export const weekRangeLabel = (weekStart) => {
  const friday = addDays(weekStart, 4);
  const sameMonth = weekStart.slice(5, 7) === friday.slice(5, 7);
  return `${sameMonth ? String(Number(weekStart.slice(8))) : formatDay(weekStart)} – ${formatDay(friday)}`;
};

// Bookings of one day merged per project/activity so "Log 3h" writes one cell per line.
export const bookingLines = (bookings, date) => {
  const lines = new Map();
  for (const b of bookings.filter((x) => x.work_date === date && (x.project_id || x.activity_type_id))) {
    const key = `${b.project_id || ""}:${b.activity_type_id || ""}`;
    const line = lines.get(key) || { key, project_id: b.project_id, activity_type_id: b.activity_type_id, title: b.project_name || b.booking_type, client: b.client_company, hours: 0 };
    line.hours += Number(b.hours_allocated) || 0;
    lines.set(key, line);
  }
  return [...lines.values()];
};
